import { useRef } from "react";
import * as _ from "lodash";
import { Datum, LineConfig, MixConfig } from "@ant-design/charts";
import { useDeepCompareCallback, useDeepCompareEffect, useDeepCompareMemo } from "use-deep-compare";
import moment from "moment";

import { ChartSeriesType, DataPointsMeta, Line, LineData } from "@ctra/api";

import { hslToHex, isDebugMode } from "@ctra/utils";
import { Enterprise as Content, useTranslation } from "@ctra/i18n";

import { useAnnotations, useChartContext, useSeriesColor } from "../../../providers";
import { useCommonConfig, useMinMax } from "../../../hooks";
import { LibraryConfigType } from "../../../typings";
import { formatXLabel } from "../../../utils";
import { SingleEntityLineChartProcessor } from "./processing/Individual";
import { MultiEntityLineChartProcessor } from "./processing/Combined";
import { Processor } from "./processing/Factory";

import "../Tooltip.css";
import "./geometries";
import "./interactions";

const {
  iofc: {
    chart: { today: todayLabel }
  }
} = Content;

/**
 * Today's date
 * @type {string}
 */
const today = moment().format("YYYY-MM-DD");

/**
 * Future region annotation - for projections
 * @type {{type: string, start: string[], end: string[], style: {fill: string, fillOpacity: number}}}
 */
const projectedRegion: NonNullable<LineConfig["annotations"]>[number] = {
  type: "region",
  start: [today, "min"],
  end: ["max", "max"],
  style: {
    fill: "#CCF9FF",
    fillOpacity: 1
  }
};

/**
 * Get the projected separator (usually "today")
 * @param {string} label
 * @returns {NonNullable<LineConfig["annotations"]>[number]}
 */
const getProjectedAreaSeparator = (label: string): NonNullable<LineConfig["annotations"]>[number] => {
  return {
    type: "line",
    start: [today, 0],
    end: [today, "max"],
    text: {
      content: label,
      position: "right",
      offsetX: 18,
      offsetY: 5,
      style: {
        textAlign: "right"
      }
    },
    style: {
      lineWidth: 2,
      lineDash: [4, 4]
    }
  };
};

/**
 * Hook that merges line config with the common config
 * The common config coming from the main app overrides the preset config
 * @return {{config: MixConfig}}
 */
export const useLineConfig = (): {
  config: MixConfig | LineConfig;
} => {
  const { commonConfig } = useCommonConfig<LineConfig>();
  const { t } = useTranslation();
  const { eventGroups } = useAnnotations();
  const chartContext = useChartContext<LineData>();

  const {
    paletteType,
    api: { getColor, setPaletteType }
  } = useSeriesColor();

  const {
    values: {
      entities,
      variants,
      config: contextConfig,
      data: d,
      meta: m,
      series: s,
      dataOptions: { bucketSize, timePeriod },
      viewOptions: { showEvents, showAnomalies }
    },
    api: { setConsolidatedData, setConsolidatedSeries }
  } = chartContext;

  /**
   * Create a ref for holding the chart config utility
   * @type {React.MutableRefObject<Processor>}
   */
  const processor = useRef<SingleEntityLineChartProcessor | MultiEntityLineChartProcessor>(
    Processor.create(entities)
  );

  /**
   * Recreate the processor when the entities change
   */
  useDeepCompareEffect(() => {
    processor.current = Processor.create(entities);
  }, [entities]);

  /**
   * Check if there are overlays. This applies to every line as line
   * charts don't have sophisticated overlays like bar charts.
   * @type {boolean}
   */
  const hasOverlays = _.size(entities) > 1;

  /**
   * Update the color manager to use one or the other palette type
   * so no pollution happens when the chart switches between overlays.
   */
  useDeepCompareEffect(() => {
    if (hasOverlays && paletteType !== ChartSeriesType.overlay) {
      setPaletteType(ChartSeriesType.overlay);
    } else {
      const [seriesType] = _.uniq(_.compact(_.map(m, "seriesType")));

      setPaletteType(seriesType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m, hasOverlays]);

  /**
   * Fix up the rounding precision to the smallest one (largest decimal setting)
   * @type {number}
   */
  const roundingPrecision = _.max(_.map(variants, "valueProperties.decimals"));

  /**
   * Get the series from the processor
   * @type {SeriesType}
   */
  const series = useDeepCompareMemo(
    () => processor.current.getSeries(m, s, variants),
    [m, s, processor.current.identity]
  );

  /**
   * Update the values for use in other context depending on the data
   */
  useDeepCompareEffect(() => {
    setConsolidatedSeries(series);
  }, [series]);

  /**
   * Merge all the data in one unorganised collection filtering out undefined values.
   * This is the big DATA for the line chart.
   * @type {Line}
   */
  const data = useDeepCompareMemo(
    () => processor.current.getData(d, m, s),
    [d, m, s, processor.current.identity]
  );

  /**
   * Update the data for use in other context depending on the data
   */
  useDeepCompareEffect(() => {
    setConsolidatedData(data);
  }, [data]);

  /**
   * Find the min and max values for the y-axis
   */
  const { min: minimum, max: maximum } = useMinMax(data, "y");

  /**
   * Show projections if the end date is after today
   */
  const showProjections = moment(timePeriod?.endDate).isAfter(moment(today).add(1, "days"));

  /**
   * Count the number of items in the legend
   * @type {number}
   */
  const isLegendVisible = processor.current.isLegendVisible(
    chartContext.values.meta,
    chartContext.values.series,
    variants
  );

  const defaultLegend = _.defaultTo(contextConfig.legend, commonConfig.legend);

  /**
   * Legend config
   * @type {{itemName: any} | boolean}
   */
  const legendConfig = isLegendVisible
    ? {
        ...defaultLegend,
        itemName: {
          ..._.get(defaultLegend, "itemName", {}),
          formatter: (text: string) => {
            return _.get(series, [text], text);
          }
        }
      }
    : false;

  /**
   * Get the metadata for the main chart
   * @type {() => any}
   */
  const getMeta = useDeepCompareCallback(() => {
    const [main] = entities;

    return _.get(m, [main.id], {} as DataPointsMeta);
  }, [m, entities]);

  /**
   * Line chart config
   */
  const lineConfig: LineConfig = useDeepCompareMemo(() => {
    const meta = getMeta();
    const yScale = _.every(_.map(m, "yAxis.yScale"), "logarithmic") ? "logarithmic" : "linear";

    const ys = _.defaultTo(
      _.get(contextConfig, ["yAxis", "type"], void 0),
      hasOverlays && yScale === "logarithmic" ? "log" : void 0
    );

    const [minRange, maxRange] = _.zip(..._.compact(_.map(m, "yAxis.range")));
    const [axisMin, axisMax] = [_.defaultTo(_.min(minRange), null), _.defaultTo(_.max(maxRange), null)];
    /**
     * Check if the data has isolated data points
     * @type {boolean}
     */
    const hasIsolatedData = _.some(m, "hasIsolatedData");

    return _.merge({}, commonConfig, {
      /**
       * Custom info for the shapes
       * @note leaving here for development purposes
       */
      customInfo: "Test: this should be accessible in registerShape in some way.",
      /**
       * Turn off the legends if there is only one series
       */
      legend: legendConfig,
      /**
       * Don't connect null values
       */
      connectNulls: false,
      /**
       * Line data
       */
      data: showAnomalies ? data : processor.current.mapAnomalies(data),
      /**
       * Pull the annotations from the context config
       */
      annotations:
        showProjections && _.every(entities, "dataProperties.maxProjectionInterval")
          ? _.compact(
              _.concat(contextConfig.annotations, [
                projectedRegion,
                getProjectedAreaSeparator(t<string>(todayLabel))
              ])
            )
          : contextConfig.annotations,
      /**
       * Tooltip settings
       */
      tooltip: _.isUndefined(contextConfig.tooltip)
        ? {
            shared: _.size(series) <= 3,
            showCrosshairs: true,
            containerTpl: `
                <div class="g2-tooltip g2-lineChart-tooltip">
                  <ul class="g2-tooltip-list"></ul>
                  <div class="g2-tooltip-title"></div>
                </div>
              `,
            crosshairs: {
              type: "x",
              line: {
                style: {
                  lineDash: [4, 4]
                }
              }
            },
            marker: {
              zIndex: 0
            },
            /**
             * Format the date/title of the tooltip
             * @param {string} value
             * @param {string} x
             * @returns {string}
             */
            title: (value: string, { x }: Line[number]) => {
              /**
               * The main chart meta will stay dominant for the tooltip
               * formatting. We don't want mixed values.
               */
              const meta: DataPointsMeta = getMeta();

              const label = formatXLabel(
                x,
                { type: meta.xType, bucketSize },
                { mayLocalise: false, mayConvertToUTC: false }
              );

              return isDebugMode() ? `${label} (${x})` : label;
            },
            /**
             * Format the tooltip values
             * @param {Datum} datum
             * @returns {{name: string, value: string | number}}
             */
            formatter: (datum: Datum): { name: string; value: string | number } => {
              /**
               * The main chart meta will stay dominant for the tooltip
               * formatting. We don't want mixed values.
               */
              const meta: DataPointsMeta = getMeta();

              return processor.current.formatTooltip(datum, meta);
            }
          }
        : contextConfig.tooltip,
      /**
       * Field settings
       */
      xField: "x",
      yField: "y",
      seriesField: "seriesField",
      /**
       * Custom shapes
       */
      lineShape: showAnomalies ? "anomaly-line" : hasIsolatedData ? "isolated-line" : "hidden-anomaly-line",
      point: showAnomalies
        ? {
            shape: "anomaly-point"
          }
        : void 0,
      /**
       * X axis styling
       */
      xAxis: {
        type: "time",
        label: {
          offset: 10
        },
        line: {
          style: {
            lineWidth: 0.5,
            stroke: "#000000"
          }
        },
        nice: false,
        tickLine: {
          length: 3,
          style: {
            lineWidth: 1,
            stroke: "#000000"
          }
        },
        /**
         * @todo keep an eye on this
         * range is changed to [0.49, 0.51] if there is only one date on the x-axis
         * this is used to center the single data points on the center of the x-axis
         */
        range: new Set(data.map((item) => item?.x)).size === 1 ? [0.49, 0.51] : [0.01, 0.99]
      },
      meta: {
        x: {
          sync: true,
          /**
           * Format the x-axis labels, but localise the date only if it is not a weekDay or month
           * @param {number} value
           * @returns {string}
           */
          formatter: (value: number): string => {
            /**
             * The main chart meta will stay dominant for the tooltip.
             * We don't want mixed values.
             */
            const meta = getMeta();

            const label = formatXLabel(
              value,
              { type: meta.xType, bucketSize },
              { mayLocalise: false, mayConvertToUTC: false }
            );

            return isDebugMode() ? `${label} (${value})` : label;
          }
        },
        y: {
          minTickInterval: roundingPrecision ? 1 / Math.pow(10, roundingPrecision) : void 0
        }
      },
      /**
       * y-axis styling
       */
      yAxis: {
        type: ys,
        label: {
          formatter: (value: string) => {
            const meta: DataPointsMeta = getMeta();

            return hasOverlays ? value : processor.current.formatYLabel(value, meta);
          }
        },
        min: _.max([_.defaultTo(axisMin, _.round(minimum - maximum * 0.02, roundingPrecision)), 0]),
        max: _.defaultTo(axisMax, _.round(maximum * 1.02, roundingPrecision)),
        title: hasOverlays
          ? void 0
          : {
              text: _.trim(meta.axis?.y?.title) || meta.yAxis?.unit
            }
      },
      /**
       * Get colors for the lines
       * @param {string} seriesField
       * @return {string}
       */
      color: ({ seriesField }: Datum) => {
        // NOTE: there is a spillover when bar (farm) switches to line chart,
        // and there needs to be some protection to not add new colors to where they don't belong.
        // It does not cause any issues, but the cache looks ugly.
        return hslToHex(getColor(void 0, seriesField));
      }
    });
  }, [data, m, entities, commonConfig, maximum, minimum, showAnomalies, contextConfig, series]);

  /**
   * Make a line for events and annotations
   * @type {LineConfig}
   */
  const farmEventLineConfig: LineConfig = useDeepCompareMemo(
    () => ({
      animation: false,
      /**
       * Add the farm event info to the points where they exist
       */
      data: processor.current.resolveEventAnnotations(data, eventGroups, showEvents),
      /**
       * Tooltip settings
       */
      tooltip: false,
      /**
       * Field settings
       */
      xField: "x",
      yField: "y",
      seriesField: "seriesField",
      /**
       * Turn off axes
       */
      xAxis: false,
      yAxis: false,
      /**
       * Point for farm events
       */
      point: {
        shape: "farm-event-point",
        state: {
          active: {
            style: () => ({
              stroke: "#007180"
            })
          },
          /**
           * Keep this empty so the interaction can be responsible for the
           * styling as this point has more components to target separately
           */
          selected: {
            style: () => ({
              stroke: "#007180",
              lineDash: [0, 0]
            })
          }
        }
      },
      /**
       * Line styling
       */
      lineStyle: {
        opacity: 0
      },
      /**
       * Hook up interactions
       */
      interactions: [
        { type: "hover-cursor" },
        { type: "element-active" },
        { type: "element-single-selected" }
      ]
    }),
    [data, eventGroups, showEvents]
  );

  /**
   * Mix chart config
   * @type {MixConfig | LibraryConfigType}
   */
  const mixConfig: MixConfig | LibraryConfigType = useDeepCompareMemo(
    () =>
      showEvents
        ? {
            syncViewPadding: true,
            legend: legendConfig,
            plots: _.compact([
              {
                type: "line",
                options: lineConfig
              },
              {
                type: "line",
                options: farmEventLineConfig
              }
            ])
          }
        : lineConfig,
    [farmEventLineConfig, lineConfig, showEvents]
  );

  return useDeepCompareMemo(
    () => ({
      config: _.mergeWith(
        // no tooltip for the chart as a whole
        {
          tooltip: false,
          ..._.omit(contextConfig, "annotations")
        },
        // use the common config without tooltip and legend,
        _.omit(commonConfig, "tooltip", "legend"),
        // add in the mix config which has tooltip definitions
        mixConfig,
        (objValue, srcValue, key) => {
          if (_.isFunction(objValue) && _.isFunction(srcValue)) {
            const whitelistedKeys = ["onReady", "onEvent"];

            return _.includes(whitelistedKeys, key) ? _.over([objValue, srcValue]) : objValue;
          }
        }
      )
    }),
    [contextConfig, commonConfig, mixConfig]
  );
};
